import { ShieldWarningIcon } from '@phosphor-icons/react'
import { match } from 'ts-pattern'
import type { VerificationResultResponse } from '../../utils/api-client/api-schemas.ts'
import { CopyButton } from '../buttons/copy-button.tsx'
import * as styles from './outcome-banner.css.ts'

const DEFAULT_COPY_LABEL = 'Copy summary'

type BannerVariant = 'spark' | 'solar' | 'drift' | 'nebula' | 'void'

export const convertVerificationResultToLabel = (result: VerificationResultResponse) =>
  match(result)
    .with('likely-exploitable', () => 'Likely exploitable')
    .with('partially-exploitable', () => 'Partially exploitable')
    .with('not-exploitable', () => 'Not exploitable')
    .with('analysis-unavailable', () => 'Analysis unavailable')
    .exhaustive()

const getVerificationBannerVariant = (result: VerificationResultResponse): BannerVariant =>
  match(result)
    .with('likely-exploitable', () => 'spark' as const)
    .with('partially-exploitable', () => 'solar' as const)
    .with('not-exploitable', () => 'nebula' as const)
    .with('analysis-unavailable', () => 'void' as const)
    .exhaustive()

const getVerificationResultIcon = (result: VerificationResultResponse) =>
  match(result)
    .with('likely-exploitable', () => <ShieldWarningIcon className={styles.warningIcon} weight="light" />)
    .with('partially-exploitable', () => (
      <ShieldWarningIcon className={styles.partiallyExploitableIcon} weight="light" />
    ))
    .with('not-exploitable', 'analysis-unavailable', () => null)
    .exhaustive()

const hasCopyButton = (result: VerificationResultResponse) =>
  match(result)
    .with('analysis-unavailable', () => false)
    .otherwise(() => true)

type VerificationOutcomeBannerProps = {
  result: VerificationResultResponse
  copyText?: string
  copyButtonTooltip?: string
}

export const VerificationOutcomeBanner: React.FC<VerificationOutcomeBannerProps> = ({
  result,
  copyText,
  copyButtonTooltip,
}) => (
  <div className={styles.banner({ variant: getVerificationBannerVariant(result) })}>
    <div className={styles.metadataGroup}>
      <span className={styles.metadataLabel}>Simulated result</span>
      <span className={styles.metadataValue}>{convertVerificationResultToLabel(result)}</span>
      {getVerificationResultIcon(result)}
    </div>
    {hasCopyButton(result) && (
      <div className={styles.copyButtonContainer}>
        <CopyButton
          copyText={copyText ?? ''}
          tooltipText={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
          label={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
          size="xlarge"
        />
      </div>
    )}
  </div>
)
